export const RESALE_ACTIVATION_MODE_ALWAYS = 'always';
export const RESALE_ACTIVATION_MODE_ONE_OR_MORE_CHANNELS_SOLD_OUT = 'one_or_more_channels_sold_out';
export const RESALE_ACTIVATION_MODE_ALL_CHANNELS_SOLD_OUT = 'all_channels_sold_out';

export type ResaleActivationMode =
  | typeof RESALE_ACTIVATION_MODE_ALWAYS
  | typeof RESALE_ACTIVATION_MODE_ONE_OR_MORE_CHANNELS_SOLD_OUT
  | typeof RESALE_ACTIVATION_MODE_ALL_CHANNELS_SOLD_OUT;

export const RESALE_ACTIVATION_MODES: ResaleActivationMode[] = [
  RESALE_ACTIVATION_MODE_ALWAYS,
  RESALE_ACTIVATION_MODE_ONE_OR_MORE_CHANNELS_SOLD_OUT,
  RESALE_ACTIVATION_MODE_ALL_CHANNELS_SOLD_OUT,
];

export const RESALE_TIME_UNIT_HOURS = 'hours';
export const RESALE_TIME_UNIT_DAYS = 'days';

export type ResaleTimeUnit = typeof RESALE_TIME_UNIT_HOURS | typeof RESALE_TIME_UNIT_DAYS;

export const RESALE_TIME_UNITS: ResaleTimeUnit[] = [RESALE_TIME_UNIT_HOURS, RESALE_TIME_UNIT_DAYS];

/**
 * The configuration shared by the company defaults and the per-event override. Every column is NOT
 * NULL with a default that reproduces the behaviour resale had before it was configurable, so only
 * `channelUuids` is nullable.
 *
 * A null `channelUuids` means nobody has picked channels yet, which the backend keeps distinct from
 * an empty list ("picked none").
 *
 * `enabled` is the pre-existing `allow_internal_resale` / `allows_internal_resale` column, not a new
 * one: the same switch Xceed staff set from admin, which the organizer now also controls from Pro.
 */
export interface ResaleSettings {
  enabled: boolean;
  activationMode: ResaleActivationMode;
  channelUuids: string[] | null;
  timeBeforeStart: number;
  timeUnit: ResaleTimeUnit;
  organizerBuyerFeePercentage: number;
}

/**
 * The event-level projection: the shared group plus two read-only fields the backend resolves.
 * `matchesCompanyDefault` compares channels as a set and the offset as the pair the organizer
 * picked, and is never derived on the client.
 */
export interface EventResaleSettings extends ResaleSettings {
  timezone: string;
  matchesCompanyDefault: boolean;
}

/** Baselines matching the column defaults, applied to a company or event that never configured resale. */
export const RESALE_DEFAULT_ACTIVATION_MODE: ResaleActivationMode = RESALE_ACTIVATION_MODE_ALWAYS;
export const RESALE_DEFAULT_TIME_BEFORE_START = 0;
export const RESALE_DEFAULT_TIME_UNIT: ResaleTimeUnit = RESALE_TIME_UNIT_HOURS;
export const RESALE_DEFAULT_ORGANIZER_FEE_PERCENTAGE = 0;

/**
 * 0 means "no extra early close", so unlike delayed delivery the offset starts at zero. The upper
 * bounds mirror delayed delivery rather than the backend's, which caps at the column ceiling
 * (2147483647) and is explicitly not a product decision.
 */
export const RESALE_MIN_TIME_BEFORE_START = 0;
export const RESALE_MAX_TIME_BEFORE_START: Record<ResaleTimeUnit, number> = {
  [RESALE_TIME_UNIT_HOURS]: 8760,
  [RESALE_TIME_UNIT_DAYS]: 365,
};

/** The organizer fee is charged to the buyer on top of Xceed's own resale fee, hence the 10% cap. */
export const RESALE_MIN_ORGANIZER_FEE_PERCENTAGE = 0;
export const RESALE_MAX_ORGANIZER_FEE_PERCENTAGE = 10;

/** The backend `number_format`s to two places without complaining, so a third is rejected here. */
export const RESALE_ORGANIZER_FEE_DECIMALS = 2;

/**
 * Which blocks of the resale form are shown, so the organizer fee can ship before the activation mode and the
 * time settings. The channels go with the activation mode, the only setting that consults them.
 */
export interface ResaleSettingsVisibility {
  activationMode: boolean;
  organizerFee: boolean;
  timeSettings: boolean;
}

export const RESALE_SETTINGS_FULLY_VISIBLE: ResaleSettingsVisibility = {
  activationMode: true,
  organizerFee: true,
  timeSettings: true,
};
